import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../stores/store';
import { useEditForm } from '../edit-form-manager';
import { useModal } from '../../../../common/modal/use-modal.hook';
import { Modals } from '../../../../constants/modals';
import { isRecordingSupported } from '../../../../common/modal-voice-recording/audio-mime.helper';
import { AudioRecordingResult } from '../../../../common/modal-voice-recording/use-audio-recorder.hook';
import { SidebarName } from '../../../../types/sidebar.types';
import { ParcelItemsAiLoadingMode } from './parcel-items-ai-loading-overlay.component';

export const useAiRecognitionPanel = (sidebarName: SidebarName) => {
  const {
    navStore: { closeSidebarByName },
    parcelItemsAiStore: {
      hasActiveReviewQueue,
      isRecognizing,
      recognizeDocument,
      recognizeVoice,
    },
    commonStore: { isGuestParcelCreation, toastInfo },
    parcelCreationStore: {
      countryDestination,
      hasPresentedAiBetaPanel,
      markAiBetaPanelPresented,
      selectedDeliveryOption,
      shipment,
    },
  } = useStore();
  const { openedItemId } = useEditForm();
  const { openModal } = useModal();
  const { t } = useTranslation();
  const [loadingMode, setLoadingMode] =
    useState<ParcelItemsAiLoadingMode | null>(null);

  const getRecognitionContext = () => ({
    destination: countryDestination?.code ?? '',
    shippingMethod:
      selectedDeliveryOption?.middle_mile.delivery_method.toUpperCase() ?? '',
  });

  const handlePhotoSelected = async (imageFile: File) => {
    setLoadingMode(ParcelItemsAiLoadingMode.Image);

    try {
      const result = await recognizeDocument(
        imageFile,
        getRecognitionContext()
      );

      if (result?.hasReviewableItems) {
        closeSidebarByName(sidebarName);
      }
    } finally {
      setLoadingMode(null);
    }
  };

  const handleScan = () => {
    openModal({
      id: Modals.PARCEL_ITEMS_PHOTO_SELECTION,
      name: Modals.PARCEL_ITEMS_PHOTO_SELECTION,
      props: {
        onSelect: handlePhotoSelected,
      },
    });
  };

  const handleRecordingComplete = async ({
    blob,
    mimeType,
  }: AudioRecordingResult) => {
    const result = await recognizeVoice(
      { audio: blob, mimeType },
      getRecognitionContext()
    );

    if (result.recognizedItemsCount === 0) {
      toastInfo(t('parcel_item_ai_audio_nothing_recognized'));
      return;
    }

    if (!result.hasReviewableItems) {
      toastInfo(t('parcel_item_ai_audio_no_categories_matched'));
      return;
    }

    closeSidebarByName(sidebarName);

    result.unmatchedItemNames.forEach((name) => {
      toastInfo(t('parcel_item_ai_audio_item_not_matched', { name }));
    });
  };

  const handleSpeak = () => {
    openModal({
      id: Modals.VOICE_RECORDING,
      name: Modals.VOICE_RECORDING,
      props: {
        onRecordingComplete: handleRecordingComplete,
      },
    });
  };

  const isEditingExistingItem =
    shipment?.items?.some((item) => item.id === openedItemId) ?? false;

  const isAiAssistantDisabled =
    loadingMode !== null ||
    isRecognizing ||
    hasActiveReviewQueue ||
    isEditingExistingItem;

  const showAiControls = !isGuestParcelCreation;
  let aiDisabledReason: string | undefined;

  if (loadingMode !== null || isRecognizing || hasActiveReviewQueue) {
    aiDisabledReason = t('parcel_items_ai_finish_review_tooltip');
  } else if (isEditingExistingItem) {
    aiDisabledReason = t('parcel_items_ai_editing_tooltip');
  }
  const canRecordVoice = isRecordingSupported();

  const showBetaPanel =
    showAiControls && !isAiAssistantDisabled && !hasPresentedAiBetaPanel;
  const searchFieldClassName = `category-search-field${showAiControls ? ' with-ai-actions' : ''}${showAiControls && canRecordVoice ? ' with-voice-action' : ''}`;

  return {
    loadingMode,
    showBetaPanel,
    dismissBetaPanel: markAiBetaPanelPresented,
    showAiControls,
    canRecordVoice,
    isAiAssistantDisabled,
    aiDisabledReason,
    handleScan,
    handleSpeak,
    searchFieldClassName,
  };
};
